/**
 * Los iconos de la página de venta (15/09/2026).
 *
 * Dibujados aquí, en línea, y no traídos de una librería: la CSP de la casa es
 * `default-src 'self'` —nada de CDN— y un paquete de iconos entero para veinte
 * dibujos engordaría el bundle de la página que se abre desde un anuncio.
 *
 * Todos van en un lienzo de 24×24, con trazo y sin relleno, y heredan el color
 * del texto (`currentColor`). Todos son decorativos: `aria-hidden`, porque el
 * texto de al lado ya dice lo que significan.
 */

type Props = { readonly tam?: number };

function Lienzo({ tam = 24, children }: Props & { readonly children: React.ReactNode }): React.ReactElement {
  return (
    <svg
      width={tam}
      height={tam}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export const IconoGlobo = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3c2.5 2.5 3.8 5.5 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.5-3.8-9S9.5 5.5 12 3z" />
    <path d="M3.5 9h17M3.5 15h17" />
  </Lienzo>
);

export const IconoFlecha = (p: Props): React.ReactElement => (
  <Lienzo {...p}><path d="M6 9l6 6 6-6" /></Lienzo>
);

export const IconoCheck = (p: Props): React.ReactElement => (
  <Lienzo {...p}><path d="M5 12.5l4.5 4.5L19 7.5" /></Lienzo>
);

export const IconoMenu = (p: Props): React.ReactElement => (
  <Lienzo {...p}><path d="M4 7h16M4 12h16M4 17h16" /></Lienzo>
);

export const IconoCerrar = (p: Props): React.ReactElement => (
  <Lienzo {...p}><path d="M6 6l12 12M18 6L6 18" /></Lienzo>
);

export const IconoMas = (p: Props): React.ReactElement => (
  <Lienzo {...p}><path d="M12 5v14M5 12h14" /></Lienzo>
);

export const IconoEscudo = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
    <path d="M9 12l2 2 4-4" />
  </Lienzo>
);

/* ── Para quién ─────────────────────────────────────────────────────────── */

export const IconoLibro = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <path d="M4 5.5C6.5 4.5 9.5 4.5 12 6c2.5-1.5 5.5-1.5 8-.5V19c-2.5-1-5.5-1-8 .5-2.5-1.5-5.5-1.5-8-.5V5.5z" />
    <path d="M12 6v13.5" />
  </Lienzo>
);

export const IconoAuriculares = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <path d="M4 15v-3a8 8 0 0 1 16 0v3" />
    <path d="M4 15h3v5H5a1 1 0 0 1-1-1v-4zM20 15h-3v5h2a1 1 0 0 0 1-1v-4z" />
  </Lienzo>
);

export const IconoMaletin = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <path d="M5 8h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <path d="M9 8V6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M4 13h16" />
  </Lienzo>
);

export const IconoMicrofono = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <path d="M12 3a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z" />
    <path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6" />
  </Lienzo>
);

export const IconoNota = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <path d="M9 18V6l10-2v12" />
    <circle cx="6.5" cy="18" r="2.5" />
    <circle cx="16.5" cy="16" r="2.5" />
  </Lienzo>
);

export const IconoMegafono = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <path d="M4 10v4h3l8 4V6l-8 4H4z" />
    <path d="M18.5 9.5a4 4 0 0 1 0 5" />
  </Lienzo>
);

export const IconoCorazon = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" />
  </Lienzo>
);

export const IconoBocadillo = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <path d="M5 5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-8l-4 3v-3H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />
  </Lienzo>
);

/* ── La voz también se entrena ──────────────────────────────────────────── */

export const IconoLista = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <path d="M9 6h11M9 12h11M9 18h11" />
    <path d="M4.5 6h.01M4.5 12h.01M4.5 18h.01" strokeWidth="2.5" />
  </Lienzo>
);

export const IconoReloj = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <circle cx="12" cy="13" r="8" />
    <path d="M12 9v4l2.5 2M9.5 2.5h5" />
  </Lienzo>
);

export const IconoPausa = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M10 9v6M14 9v6" />
  </Lienzo>
);

/* ── Por qué Voice Comfort ──────────────────────────────────────────────── */

export const IconoSello = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <circle cx="12" cy="10" r="6" />
    <path d="M9.5 10l1.8 1.8L14.8 8.3M8.5 15l-1.5 6 5-2.5 5 2.5-1.5-6" />
  </Lienzo>
);

export const IconoAltavoz = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <path d="M4 9v6h4l5 4V5L8 9H4z" />
    <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" />
  </Lienzo>
);

export const IconoOnda = (p: Props): React.ReactElement => (
  <Lienzo {...p}><path d="M3 12h3l2-5 4 10 3-7 2 2h4" /></Lienzo>
);

export const IconoTexto = (p: Props): React.ReactElement => (
  <Lienzo {...p}><path d="M5 6h14M5 10h14M5 14h9M5 18h6" /></Lienzo>
);

export const IconoLuna = (p: Props): React.ReactElement => (
  <Lienzo {...p}><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" /></Lienzo>
);

export const IconoGrafica = (p: Props): React.ReactElement => (
  <Lienzo {...p}><path d="M4 19h16M6 15l4-4 3 3 5-6M15 8h3v3" /></Lienzo>
);

/* ── Tu seguridad, primero ──────────────────────────────────────────────── */

export const IconoPortapapeles = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <path d="M9 4h6v3H9z" />
    <path d="M8 5.5H6a1 1 0 0 0-1 1V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6.5a1 1 0 0 0-1-1h-2" />
    <path d="M9 13.5l2 2 4-4" />
  </Lienzo>
);

export const IconoBrujula = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M15.5 8.5l-2 5-5 2 2-5 5-2z" />
  </Lienzo>
);

export const IconoHoja = (p: Props): React.ReactElement => (
  <Lienzo {...p}>
    <path d="M5 19c0-8 6-14 14-14 0 8-6 14-14 14z" />
    <path d="M5 19l8-8" />
  </Lienzo>
);
